import { LotAssertionWrite } from '~/app/accounting/lot-assertion-writes'

export type LotAssertionKind = LotAssertionWrite['kind']

export type LotAssertionAuthor = {
  id: string
  email: string
  displayName: string | null
  profilePhotoUrl: string | null
}

export type AssertedValues = {
  /** USD per whole token, decimal string. */
  unitPrice: string | null
  /** ISO 8601 timestamp. */
  acquisitionTime: string | null
  /** Raw base units as an integer string. */
  quantity: string | null
  originLotId: string | null
}

export type LotAssertionHistoryItem = AssertedValues & {
  id: string
  targetKey: string
  kind: LotAssertionKind
  createdAt: string
  createdBy: LotAssertionAuthor | null
  // The values in force at this row's target key just before it was written;
  // null when nothing was asserted yet and the derived values applied.
  previous: AssertedValues | null
}

export type HistoryField = keyof AssertedValues

/** Null on either side means the derived value, not an asserted one. */
export type HistoryChange = {
  field: HistoryField
  from: string | null
  to: string | null
}

export type LotHistoryEntry = {
  id: string
  title: string
  createdAt: string
  createdBy: LotAssertionAuthor | null
  changes: HistoryChange[]
}

const FIELDS: HistoryField[] = [
  'unitPrice',
  'acquisitionTime',
  'quantity',
  'originLotId',
]

// Every field whose value differs from what was in force before the row. The
// latest row replaces the prior one wholesale, so a field the earlier edit
// asserted and this one omits reads as reverting to derived.
const changesBetween = (
  previous: AssertedValues | null,
  current: AssertedValues,
): HistoryChange[] =>
  FIELDS.flatMap((field) => {
    const from = previous?.[field] ?? null
    const to = current[field]
    return from === to ? [] : [{ field, from, to }]
  })

const titleFor = ({
  kind,
  previousKind,
}: {
  kind: LotAssertionKind
  previousKind: LotAssertionKind | undefined
}): string => {
  switch (kind) {
    case 'seed':
      return previousKind === 'seed' ? 'Updated seed' : 'Seeded lot'
    case 'edit':
      return 'Edited lot'
    case 'designate':
      return 'Marked source transfer as migration'
    case 'revoke':
      if (previousKind === 'designate') return 'Unmarked migration transfer'
      if (previousKind === 'seed') return 'Removed seed'
      return 'Revoked edits'
  }
}

/**
 * Newest first. What a revoke withdrew depends on what came before it at the
 * same target key, so each key's previous kind is tracked while mapping.
 */
export const buildLotHistoryEntries = (
  items: LotAssertionHistoryItem[],
): LotHistoryEntry[] => {
  const previousKindByTargetKey = new Map<string, LotAssertionKind>()
  return items
    .map((item): LotHistoryEntry => {
      const previousKind = previousKindByTargetKey.get(item.targetKey)
      previousKindByTargetKey.set(item.targetKey, item.kind)
      return {
        id: item.id,
        title: titleFor({ kind: item.kind, previousKind }),
        createdAt: item.createdAt,
        createdBy: item.createdBy,
        changes: changesBetween(item.previous, item),
      }
    })
    .reverse()
}
